import { useId } from "react";
import type { ReactNode } from "react";
import { cx } from "../ui/primitives";
import { IconChevron } from "../ui/icons";
import { hintTitle } from "./hintTitle";

/**
 * The selectable tag bubble, and the collapsible group it sits in.
 *
 * Browse and the region editor were two different answers to the same question — "which
 * of these keywords do you want" — drawn two different ways: Browse as a column of full
 * width rows, the editor as wrapped bubbles. Same vocabulary, same facets, same act,
 * and nothing but history separating them. A keyword that reads as a bubble in one place
 * and a list row in the other teaches that they are different kinds of thing.
 *
 * One component, so a change to what "selected" looks like lands on both.
 *
 * What is deliberately NOT shared is the selection RULE. Browse takes one value per facet
 * (picking a second replaces the first); the editor takes any number (picks in a group
 * widen the region). Both are expressed by the caller through `active` and `onClick`,
 * which is the honest split: the chip renders a state, it does not own the policy.
 */
export function TagChip({
  label,
  hint,
  active,
  disabled = false,
  onClick,
}: {
  label: string;
  /** Explanatory text for a value whose name does not say enough on its own — "Short"
   *  means nothing until it says ">7–15s". Lands in this button's title, so hovering the
   *  chip shows `label — hint`. */
  hint?: string | null;
  active: boolean;
  /**
   * Nothing left under the current filter: visible, but inert.
   *
   * Kept on screen rather than removed, because the shape of a facet is information —
   * "no trans clip carries a race keyword" is a true and useful thing to see — and a
   * list that silently reflows as you filter is hard to aim at.
   */
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={hintTitle(label, hint)}
      className={cx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-accent)]/20 text-[var(--color-accent-soft)]"
          : disabled
            ? "cursor-default border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-600 opacity-50"
            : "border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-300 hover:border-[var(--color-accent)]/60",
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      {/* The per-keyword clip count used to sit here. Gone: the number was catalog
          trivia next to every label, and it made the bubble field read as a table.
          Availability still reaches the chip through `disabled` — a zero-count value
          dims, it just doesn't print the zero. */}
      {/* The "?" hint badge was here. Removed for the same reason as on TagBubble: this
          chip's own `title` is already `hintTitle(label, hint)`, so hovering the chip
          shows the label AND the explanation, and the badge was a smaller target offering
          less. It also had to be inert and aria-hidden to avoid nesting a focusable
          element inside this button — a constraint that now simply does not exist. */}
    </button>
  );
}

/**
 * A collapsible group of chips: a disclosure header over a wrapped bubble field.
 *
 * Everything starts closed where the caller says so, which turns a column of fifty-odd
 * values into a handful of labels. Opening the one axis you care about is cheap;
 * scrolling past the five you don't, every visit, is not.
 *
 * `summary` is what the group is currently filtering by, and shows only while CLOSED.
 * Collapsing may hide the alternatives; it must never hide the answer. Once open the
 * selected chips are right there in accent, and repeating them in the header is the same
 * words twice.
 */
export function ChipGroup({
  label,
  summary,
  open,
  onToggle,
  badge,
  children,
}: {
  label: string;
  /** The current selection, for the closed state. Null when nothing is picked. */
  summary: string | null;
  open: boolean;
  onToggle: () => void;
  /** Something small beside the label — the New! badge a freshly added group wears. */
  badge?: ReactNode;
  children: ReactNode;
}) {
  const fieldId = useId();
  return (
    <div className="min-w-0">
      <h2>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={fieldId}
          className="flex w-full items-center gap-1.5 whitespace-nowrap rounded-md py-1 pr-1 text-left transition-colors hover:text-zinc-200"
        >
          <IconChevron
            width="12"
            height="12"
            className={cx(
              "shrink-0 text-zinc-600 transition-transform",
              open ? "" : "-rotate-90",
            )}
          />
          <span
            className={cx(
              "text-[11px] font-semibold uppercase tracking-widest",
              summary ? "text-zinc-300" : "text-zinc-500",
            )}
          >
            {label}
          </span>
          {badge}
          {summary && !open ? (
            <span className="ml-auto truncate pl-2 text-xs text-[var(--color-accent-soft)]">
              {summary}
            </span>
          ) : null}
        </button>
      </h2>

      {open ? (
        <div id={fieldId} className="mt-1.5 flex flex-wrap gap-1.5">
          {children}
        </div>
      ) : null}
    </div>
  );
}
